import React, { useState, useMemo, useEffect } from 'react';
import { Activity, RefreshCw, BarChart3, Plus } from 'lucide-react';
import { DashboardProvider, useDashboard } from './context/DashboardContext';
import MetricsOverview from './components/MetricsOverview';
import DashboardTabs from './components/DashboardTabs';
import Settings from './components/Settings';
import Logo from './components/Logo';
import ErrorBanner from './components/ErrorBanner';
import ApiKeyPrompt from './components/ApiKeyPrompt';
import LoginScreen from './components/LoginScreen';
import './App.css';

// Memoized AppContent component for better performance
const AppContent = React.memo(({ onLogout }) => {
  const { 
    loading, 
    error, 
    lastUpdated, 
    refreshData, 
    handleEnvironmentChange, 
    isRefreshing, 
    currentEnvironment,
    showApiKeyPrompt,
    handleApiKeySubmit
  } = useDashboard();
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [dismissedError, setDismissedError] = useState(null);

  console.log('AppContent render:', { loading, error, lastUpdated, isRefreshing, currentEnvironment, showApiKeyPrompt });

  // Memoize navigation items to prevent unnecessary re-renders
  const navigationItems = useMemo(() => [
    {
      id: 'dashboard',
      label: 'Dashboard',
      icon: BarChart3,
      active: currentPage === 'dashboard'
    },
    {
      id: 'settings',
      label: 'Settings',
      icon: Plus,
      active: currentPage === 'settings'
    }
  ], [currentPage]);

  // Handle error dismissal
  const handleErrorDismiss = () => {
    setDismissedError(error);
  };

  // Show error banner if there's an error and it hasn't been dismissed
  const showErrorBanner = error && error !== dismissedError;

  // Show API key prompt if needed
  if (showApiKeyPrompt) {
    return <ApiKeyPrompt onApiKeySubmit={handleApiKeySubmit} />;
  }

  if (loading) {
    console.log(`${currentEnvironment}: Showing loading state`);
    return (
      <div className="loading">
        <Activity className="animate-spin" size={24} />
        <span style={{ marginLeft: '0.5rem' }}>{currentEnvironment}: Loading data...</span>
      </div>
    );
  }

  return (
    <div className="App">
      {showErrorBanner && (
        <ErrorBanner 
          error={error} 
          onDismiss={handleErrorDismiss}
        />
      )}
      
      <header className="app-header">
        <div className="container">
          <div className="header-content">
            <div className="header-left">
              <div className="header-brand">
                <Logo size={48} className="header-logo" />
                <div className="header-text">
                  <h1>FlightPath</h1>
                  <p>Navigate your projects with precision</p>
                </div>
              </div>
            </div>
            <div className="header-right">
              <div className="header-actions">
                <nav className="main-nav">
                  {navigationItems.map(({ id, label, icon: Icon, active }) => (
                    <button 
                      key={id}
                      className={`nav-btn ${active ? 'active' : 'inactive'}`}
                      onClick={() => setCurrentPage(id)}
                    >
                      <Icon size={16} />
                      {label}
                    </button>
                  ))}
                </nav>
                <button 
                  onClick={refreshData} 
                  className="refresh-button"
                  disabled={isRefreshing}
                  title={isRefreshing ? 'Refreshing...' : 'Refresh data'}
                >
                  <RefreshCw size={16} className={isRefreshing ? 'animate-spin' : ''} />
                  {isRefreshing ? 'Refreshing...' : 'Refresh'}
                </button>
                <div className="last-updated">
                  Last updated: {lastUpdated ? lastUpdated.toLocaleString() : 'Never'}
                </div>
                <button 
                  onClick={onLogout} 
                  className="logout-button"
                  title="Logout"
                >
                  <span>Logout</span>
                </button>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main className="container">
        {currentPage === 'dashboard' && (
          <>
            <MetricsOverview />
            <DashboardTabs />
          </>
        )}
        {currentPage === 'settings' && <Settings />}
      </main>
    </div>
  );
});

AppContent.displayName = 'AppContent';

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  useEffect(() => {
    // Check if user is already authenticated
    const checkAuth = () => {
      const authenticated = localStorage.getItem('flightpath-authenticated');
      const authTimestamp = localStorage.getItem('flightpath-auth-timestamp');
      
      if (authenticated === 'true' && authTimestamp) {
        // Check if authentication is still valid (24 hours)
        const now = Date.now();
        const authTime = parseInt(authTimestamp);
        const hoursSinceAuth = (now - authTime) / (1000 * 60 * 60);
        
        if (hoursSinceAuth < 24) {
          setIsAuthenticated(true);
        } else {
          // Clear expired authentication
          localStorage.removeItem('flightpath-authenticated');
          localStorage.removeItem('flightpath-auth-timestamp');
        }
      }
      
      setIsCheckingAuth(false);
    };

    checkAuth();
  }, []);

  const handleLogin = () => {
    setIsAuthenticated(true);
  };

  const handleLogout = () => {
    localStorage.removeItem('flightpath-authenticated');
    localStorage.removeItem('flightpath-auth-timestamp');
    setIsAuthenticated(false);
  };

  // Show loading while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="loading">
        <Activity className="animate-spin" size={24} />
        <span style={{ marginLeft: '0.5rem' }}>Checking authentication...</span>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  return (
    <DashboardProvider>
      <AppContent onLogout={handleLogout} />
    </DashboardProvider>
  );
}

export default App;
